import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, TrendingDown, Search, Activity, Clock, GitCompare, LineChart as LineIcon, Store, MessagesSquare } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import CommodityIcon from "@/components/CommodityIcon";
import AnimatedPrice from "@/components/AnimatedPrice";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import { RowSkeleton } from "@/components/Skeletons";
import PriceHistoryChart from "@/components/PriceHistoryChart";
import TradeBoard from "@/components/TradeBoard";
import CommodityForum from "@/components/CommodityForum";
import { useAuth } from "@/context/AuthContext";
import { fmtInTz } from "@/lib/utils";

const CATS = ["All", "Metals", "Precious", "Energy", "Agriculture"];
const TABS = [
  { id: "chart", label: "Chart", icon: LineIcon },
  { id: "board", label: "Trade Board", icon: Store },
  { id: "forum", label: "Forum", icon: MessagesSquare },
];

export default function PriceIndex() {
  const { symbol } = useParams();
  if (symbol) return <PriceDetail symbol={symbol} />;
  return <PriceList />;
}

function PriceList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const { data: full, isLoading } = useQuery({
    queryKey: ["prices-full"],
    queryFn: async () => (await api.get("/prices")).data,
    refetchInterval: 30000,
  });

  const items = (full?.items || [])
    .filter((p) => cat === "All" || p.category === cat)
    .filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="sticky top-0 z-30 glass px-4 pb-3 pt-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => nav(-1)} data-testid="prices-back" className="pressable glass flex h-10 w-10 items-center justify-center rounded-full">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="font-display text-2xl font-extrabold leading-none">Price Index</h1>
              <span className="mt-1 flex items-center gap-1 text-[11px] font-bold" style={{ color: "var(--green)" }} data-testid="prices-last-updated">
                <span className="live-dot h-1.5 w-1.5 rounded-full" style={{ background: "var(--green)" }} />
                Updated {fmtInTz(full?.last_fetched, user?.timezone, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => nav("/compare")} data-testid="open-compare" className="pressable glass flex h-10 w-10 items-center justify-center rounded-full">
              <GitCompare size={17} className="accent" />
            </button>
            <CurrencySwitcher />
          </div>
        </div>
        <div className="glass flex items-center gap-2 rounded-2xl px-4 py-2.5">
          <Search size={17} className="text-dim" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="prices-search" placeholder="Search commodity…"
                 className="w-full bg-transparent text-sm outline-none placeholder:text-dim" style={{ color: "var(--text)" }} />
        </div>
      </div>

      <div className="no-scrollbar mt-3 flex gap-2 overflow-x-auto px-4">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-active={cat === c} data-testid={`price-cat-${c}`}
                  className="chip pressable shrink-0 px-4 py-2 text-sm font-semibold">{c}</button>
        ))}
      </div>

      <div className="mt-4 space-y-2 px-4">
        {isLoading
          ? [0, 1, 2, 3, 4].map((i) => <div key={i} className="goe-card"><RowSkeleton /></div>)
          : items.map((p, i) => {
              const up = p.change_pct >= 0;
              return (
                <motion.button key={p.symbol}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}
                  whileTap={{ scale: 0.98 }} onClick={() => nav(`/prices/${p.symbol}`)} data-testid={`price-row-${p.symbol}`}
                  className="goe-card lift flex w-full items-center gap-3 p-3 text-left">
                  <CommodityIcon name={p.name} size={22} />
                  <div className="flex-1">
                    <p className="font-display font-bold">{p.name}</p>
                    <p className="text-xs text-dim">
                      {p.unit}
                      {!p.live && <span className="ml-1.5 rounded px-1 py-0.5 text-[9px] font-bold" style={{ background: "var(--surface-2)" }}>INDICATIVE</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <AnimatedPrice amount={p.price} from="USD" className="font-display block text-lg font-extrabold" />
                    <span className="flex items-center justify-end gap-0.5 text-xs font-bold" style={{ color: up ? "var(--green)" : "var(--red)" }}>
                      {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{up ? "+" : ""}{p.change_pct}%
                    </span>
                  </div>
                </motion.button>
              );
            })}
      </div>
    </motion.div>
  );
}

function PriceDetail({ symbol }) {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "chart";
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["price", symbol],
    queryFn: async () => (await api.get(`/prices/${symbol}`)).data,
    refetchInterval: 30000,
  });

  if (isLoading || !data) {
    return (
      <div className="p-4">
        <div className="skeleton mb-4 h-10 w-10 rounded-full" />
        <div className="skeleton mb-2 h-8 w-40" />
        <div className="skeleton h-56 w-full" />
      </div>
    );
  }

  const up = data.change_pct >= 0;
  const color = up ? "#34D07F" : "#FF5C5C";

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
      <div className="sticky top-0 z-30 glass px-4 pb-3 pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/prices")} data-testid="detail-back" className="pressable glass flex h-10 w-10 items-center justify-center rounded-full">
              <ArrowLeft size={18} />
            </button>
            <div className="flex items-center gap-2">
              <CommodityIcon name={data.name} size={18} />
              <h1 className="font-display text-xl font-extrabold">{data.name}</h1>
            </div>
          </div>
          <CurrencySwitcher />
        </div>
        <div className="mt-3 flex rounded-2xl p-1" style={{ background: "var(--surface-2)" }}>
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setParams({ tab: t.id }, { replace: true })} data-testid={`commodity-tab-${t.id}`}
                    className="pressable relative flex flex-1 items-center justify-center rounded-xl py-2 text-xs font-bold"
                    style={{ color: tab === t.id ? "#fff" : "var(--text-dim)" }}>
              {tab === t.id && <motion.div layoutId="ctab" className="absolute inset-0 rounded-xl" style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)" }} />}
              <span className="relative flex items-center gap-1.5"><t.icon size={14} />{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-5">
        <div className="flex items-end justify-between">
          <div>
            <AnimatedPrice amount={data.price} from="USD" className="font-display text-4xl font-extrabold" />
            <p className="mt-1 text-sm text-dim">{data.unit} · {data.category}</p>
          </div>
          <span className="flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-bold"
                style={{ background: up ? "rgba(52,208,127,0.15)" : "rgba(255,92,92,0.15)", color }} data-testid="detail-change">
            {up ? <TrendingUp size={15} /> : <TrendingDown size={15} />}{up ? "+" : ""}{data.change_pct}%
          </span>
        </div>
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-dim" data-testid="detail-updated">
          <Clock size={12} /> Last updated {fmtInTz(data.updated, user?.timezone)}
        </p>

        <div className="mt-5 pb-6">
          {tab === "chart" && (
            <>
              <PriceHistoryChart symbol={symbol} />
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Stat label="24h Change" value={`${up ? "+" : ""}${data.change_abs}`} color={color} />
                <Stat label="Source" value={data.live ? "Market feed" : "Indicative"} />
              </div>
              <div className="goe-card mt-3 flex items-start gap-2 p-4 text-sm text-dim">
                <Activity size={16} className="accent mt-0.5 shrink-0" />
                {data.live
                  ? "Real futures prices from Yahoo Finance, refreshed at 08:00 and 20:00 UTC."
                  : "No free live market feed exists for this commodity — showing an indicative reference price."}
              </div>
              <button onClick={() => nav("/compare")} data-testid="detail-compare"
                      className="glass pressable mt-3 flex w-full items-center justify-center gap-2 rounded-2xl py-3 text-sm font-bold accent">
                <GitCompare size={16} /> Compare with other commodities
              </button>
            </>
          )}
          {tab === "board" && <TradeBoard symbol={symbol} name={data.name} />}
          {tab === "forum" && <CommodityForum symbol={symbol} name={data.name} />}
        </div>
      </div>
    </motion.div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="goe-card p-4">
      <p className="text-xs font-semibold text-dim">{label}</p>
      <p className="font-display mt-1 text-lg font-extrabold" style={{ color: color || "var(--text)" }}>{value}</p>
    </div>
  );
}
